import React, { useEffect, useRef, useState } from 'react';
import { shortenAddress } from '../../../utils/shortenAddress';
import uniqueId from '../../../utils/uniqueId';
import Tooltip from '../../ui/Tooltip';
import styles from './TruncatedText.module.scss';

const TruncatedText = ({ text, className, noTooltip = false }) => {
  const toolTipId = useRef(uniqueId('truncated'));
  const visibleSpanRef = useRef(null);
  const measureSpanRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const shortText = shortenAddress(text, 6, 4, true);
  const isShortenedByFormat = shortText !== text;
  const textToDisplay =
    isShortenedByFormat || isTruncated ? shortText : text;

  const checkTruncation = () => {
    if (isShortenedByFormat) {
      return;
    }

    const measureElement = measureSpanRef.current;

    if (measureElement) {
      const isOverflowing =
        measureElement.scrollWidth > measureElement.clientWidth;

      if (isOverflowing !== isTruncated) {
        setIsTruncated(isOverflowing);
      }
    }
  };

  useEffect(() => {
    const visibleElement = visibleSpanRef.current;

    const handleResize = () => {
      if (visibleElement) {
        setContainerWidth(visibleElement.clientWidth);
      }
    };

    handleResize();

    let observer;
    if (visibleElement && typeof window.ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(handleResize);
      observer.observe(visibleElement);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [text]);

  useEffect(() => {
    checkTruncation();
  }, [containerWidth, text, isTruncated, isShortenedByFormat]);

  const shouldShowTooltip = !noTooltip && (isShortenedByFormat || isTruncated);

  const tooltipProps = shouldShowTooltip
    ? { 'data-for': toolTipId.current, 'data-tip': true }
    : {};

  return (
    <>
      <span
        ref={visibleSpanRef}
        className={`${styles.text} ${className || ''}`}
        {...tooltipProps}
      >
        {textToDisplay}
      </span>

      <span
        ref={measureSpanRef}
        className={styles.text}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: 'none',
          width: containerWidth > 0 ? `${containerWidth}px` : 'auto',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
        aria-hidden="true"
      >
        {text}
      </span>

      {shouldShowTooltip ? (
        <Tooltip id={toolTipId.current} info={text} />
      ) : null}
    </>
  );
};

export default TruncatedText;
